import { useState, useEffect, useCallback, useMemo } from 'react';
import { APP_CONFIG } from '../config';
import { Order, OrbOrderStatus } from '../types/orderTypes';
import type { Docket } from '@oolio-group/domain';
import {
  aggregateDocketsToOrders,
  filterDocketsWithinConfiguredTimeframe,
  filterOrdersByType,
} from '../helpers/docketHelpers';
import { usePolling } from '../libs/polling/usePolling';
import { storageSvc } from '@/services/storageServiceClient';
import { useSelection } from '@/libs/onboarding';
import { useDeviceConfig } from '@/hooks/useDeviceConfig';
import { fetchDocketsFromAPI } from '@/services/docketService';

export interface DocketResponse {
  dockets: Docket[];
}

export interface UseDocketsReturn {
  preparing: Order[];
  ready: Order[];
  loading: boolean;
  error: string | null;
}

export const useDockets = (): UseDocketsReturn => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { store, organization } = useSelection(storageSvc);
  const {
    orbConfig,
    getSalesChannelName,
    loading: deviceConfigLoading,
  } = useDeviceConfig();

  /**
   * Fetch dockets from API with two concurrent calls (CREATED and COMPLETED)
   *
   * The docket service requires a 'status' parameter in the request payload and can only
   * return dockets of a single status per API call. Since ORB needs to display both:
   * - CREATED dockets in the "Preparing..." column
   * - COMPLETED dockets in the "Ready!" column
   *
   * We make two concurrent API calls using Promise.all to fetch both statuses
   * simultaneously, then combine the results.
   *
   */
  const fetchDockets = useCallback(async (): Promise<void> => {
    if (!store?.id || !organization?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const dockets = await fetchDocketsFromAPI({
        store,
        orbConfig,
        organization,
      });
      const processedOrders = processDockets(dockets);
      setOrders(processedOrders);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch dockets';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [store, organization, getSalesChannelName, orbConfig]);

  // processDockets transforms the raw dockets into a structured order format
  // It filters dockets based on the configured timeframe and aggregates them into orders
  // It also applies any order type filtering based on the orbConfig
  // Finally, it filters out orders that have elapsed to ensure only relevant orders are displayed
  const processDockets = (allDockets: Docket[]): Order[] => {
    if (allDockets.length === 0) {
      return [];
    }
    const recentDockets = filterDocketsWithinConfiguredTimeframe(allDockets);
    const transformedOrders = aggregateDocketsToOrders({
      dockets: recentDockets,
      getSalesChannelName,
      timeoutMs:
        (orbConfig.autoClearTimeout ?? APP_CONFIG.AUTO_CLEAR_TIMEOUT_SECONDS) *
        1000, // Convert seconds to milliseconds
    });
    const orderTypeFilteredOrders = filterOrdersByType(
      transformedOrders,
      orbConfig.orderTypeIds ?? [],
    );
    // Filter out orders that have elapsed
    // This ensures we do not show orders that are no longer relevant
    return orderTypeFilteredOrders.filter(
      order => order.status !== OrbOrderStatus.ELAPSED,
    );
  };

  const { startPolling, stopPolling } = usePolling({
    callback: fetchDockets,
    interval: APP_CONFIG.POLLING_INTERVAL,
  });

  // Setup polling interval
  useEffect(() => {
    // Initial fetch
    fetchDockets();

    // Start polling
    startPolling();

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [fetchDockets, startPolling, stopPolling]);

  /**
   * Sort orders by status-specific criteria
   * - Preparing orders: oldest first to prioritize customers who have waited longest
   * - Ready orders: newest first to ensure fresh orders are served immediately
   */
  const sortedPreparingOrders = useMemo(() => {
    return [...orders]
      .filter(order => order.status === OrbOrderStatus.PREPARING)
      .sort((a, b) => a.createdAt - b.createdAt); // oldest first - customers wait longer should be prioritized
  }, [orders]);

  const sortedReadyOrders = useMemo(() => {
    return [...orders]
      .filter(order => order.status === OrbOrderStatus.READY)
      .sort((a, b) => b.updateTime - a.updateTime); // latest completed first - show newest ready orders
  }, [orders]);

  return {
    preparing: sortedPreparingOrders,
    ready: sortedReadyOrders,
    loading: deviceConfigLoading || loading,
    error,
  };
};
